import {
  useGetCurrentInstance,
  useUpdateCurrentInstance,
  type InstanceWatermarkConfig,
} from "@/api/instance";
import {
  useGetOrCreateCrEnsemble,
  useGetPermanentEnsembles,
  useUpdatePermanentEnsemble,
} from "@/api/permanent-ensemble";
import {
  useGetAvailableFonts,
  useGetGenericImageSettings,
  useGetPrintFont,
  useUpdateGenericImageSettings,
  useUpdatePrintFont,
  type GenericImageAlignment,
  type GenericImageSettings,
  type PrintFontFamily,
} from "@/api/user-settings";
import CertificateLogoUpload from "@/components/form-fields/CertificateLogoUpload";
import * as AF from "@/components/form-items/AbstractForm";
import { GeneralSettingsTab } from "@/components/settings/general-settings-tab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { PermanentEnsemble } from "@/lib/types";
import { useEffect } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

const VALID_WATERMARK_POSITIONS = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
  "center",
] as const;
const CORNER_POSITIONS = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
];
const VALID_WATERMARK_FONTS = ["arial", "helvetica", "times"] as const;
const VALID_WATERMARK_FONT_WEIGHTS = ["normal", "bold"] as const;

function normalizePosition(
  value: string | null | undefined,
): (typeof VALID_WATERMARK_POSITIONS)[number] {
  const v = value?.trim();
  if (
    v &&
    VALID_WATERMARK_POSITIONS.includes(
      v as (typeof VALID_WATERMARK_POSITIONS)[number],
    )
  ) {
    return v as (typeof VALID_WATERMARK_POSITIONS)[number];
  }
  return "bottom-right";
}

function normalizeFont(
  value: string | null | undefined,
  fallback: (typeof VALID_WATERMARK_FONTS)[number],
): (typeof VALID_WATERMARK_FONTS)[number] {
  const v = value?.trim();
  if (
    v &&
    VALID_WATERMARK_FONTS.includes(v as (typeof VALID_WATERMARK_FONTS)[number])
  ) {
    return v as (typeof VALID_WATERMARK_FONTS)[number];
  }
  return fallback;
}

function normalizeFontWeight(
  value: string | null | undefined,
): (typeof VALID_WATERMARK_FONT_WEIGHTS)[number] {
  const v = value?.trim();
  if (
    v &&
    VALID_WATERMARK_FONT_WEIGHTS.includes(
      v as (typeof VALID_WATERMARK_FONT_WEIGHTS)[number],
    )
  ) {
    return v as (typeof VALID_WATERMARK_FONT_WEIGHTS)[number];
  }
  return "normal";
}

function getWatermarkDefaultValues(instance: {
  watermarkEnabled?: boolean | null;
  watermarkText?: string | null;
  watermarkPosition?: string | null;
  watermarkFontFamily?: string | null;
  watermarkFontWeight?: string | null;
  watermarkFontSize?: number | null;
  watermarkRotate90?: boolean | null;
  watermarkOpacity?: number | null;
}): InstanceWatermarkConfig {
  return {
    watermarkEnabled: instance.watermarkEnabled ?? false,
    watermarkText: instance.watermarkText ?? "",
    watermarkPosition: normalizePosition(instance.watermarkPosition),
    watermarkFontFamily: normalizeFont(instance.watermarkFontFamily, "arial"),
    watermarkFontWeight: normalizeFontWeight(instance.watermarkFontWeight),
    watermarkFontSize: instance.watermarkFontSize ?? 24,
    watermarkRotate90: instance.watermarkRotate90 ?? false,
    watermarkOpacity: instance.watermarkOpacity ?? 85,
  };
}

type EnsembleSettingsProps = {
  form: UseFormReturn<PermanentEnsemble>;
  printFontForm: UseFormReturn<{ fontFamily: PrintFontFamily }>;
};

const FONT_OPTIONS = [
  { value: "Arial", label: "Arial" },
  { value: "Roboto", label: "Roboto" },
  { value: "Raleway", label: "Raleway" },
  { value: "Merriweather", label: "Merriweather" },
  { value: "Lora", label: "Lora" },
  { value: "Spectral", label: "Spectral" },
];

type PrintSettingsTabProps = {
  form: UseFormReturn<{ fontFamily: PrintFontFamily }>;
};

const PrintSettingsTab = ({ form }: PrintSettingsTabProps) => {
  const vt = useVisibleT();

  return (
    <div className="flex flex-col gap-5 w-full max-w-[800px]">
      <div className="space-y-2">
        <Label
          htmlFor="fontFamily"
          className="text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
        >
          {vt("printSettings.font", "Police d'impression")}
        </Label>
        <Select
          value={form.watch("fontFamily")}
          onValueChange={(v) =>
            form.setValue("fontFamily", v as PrintFontFamily, {
              shouldDirty: true,
            })
          }
        >
          <SelectTrigger id="fontFamily" className="max-w-[300px] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const EnsembleSettings = ({ form, printFontForm }: EnsembleSettingsProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab");

  return (
    <div className="flex gap-12 items-start">
      <div className="max-w-[800px] w-full">
        <AF.AbstractForm
          form={form}
          formKey="settings"
          tabs={[
            { value: "generalInformation", label: t("artworksGeneralInfo") },
            { value: "documents", label: t("entryDocuments") },
            { value: "contact", label: t("contactFormTitle") },
            { value: "printSettings", label: vt("printSettings", "Impression") },
          ]}
          classname="p-0 m-0"
        >
          <AF.Tab value="generalInformation" showHeader={false}>
            <AF.Col>
              <AF.Text
                name="ensemblesDescriptionNomEnsemble"
                label={vt("bo.settings.cr.nom", "Nom du CR")}
              />
              <Separator className="my-2" />
              <AF.Text
                name="ensemblesDescriptionAuteurEnsemble"
                label={vt("bo.settings.cr.auteur", "Auteur du CR")}
              />
              <Separator className="my-2" />
              <AF.Text
                name="ensemblesDescriptionOperateurPrincipalEnsemble"
                label={vt(
                  "bo.settings.cr.operateurPrincipal",
                  "Utilisateur principal du CR",
                )}
              />
              <Separator className="my-2" />
              <AF.Text name="ensemblesDescriptionOperateurAutre" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesDescriptionSignataire" />
            </AF.Col>
          </AF.Tab>
          <AF.Tab value="documents" showHeader={false}>
            <AF.Col>
              <AF.Text name="ensemblesTitreDocument1" />
              <Separator className="my-2" />
              <AF.TextArea className="h-40" name="ensemblesText1Document1" />
              <Separator className="my-2" />
              <AF.TextArea className="h-40" name="ensemblesText2Document1" />
              <Separator className="my-2" />

              <AF.Text name="ensemblesTitreDocument2" />
              <Separator className="my-2" />
              <AF.TextArea className="h-40" name="ensemblesText1Document2" />
              <Separator className="my-2" />
              <AF.TextArea className="h-40" name="ensemblesText2Document2" />
              <Separator className="my-2" />
              <AF.Text
                name="ensemblesTitreDocument3"
                label={vt("ensemblesTitreDocument3", "Titre du document 3")}
              />
              <Separator className="my-2" />
              <AF.TextArea
                className="h-40"
                name="ensemblesText1Document3"
                label={vt("ensemblesText1Document3", "Texte 1 du document 3")}
              />
              <Separator className="my-2" />
              <AF.TextArea
                className="h-40"
                name="ensemblesText2Document3"
                label={vt("ensemblesText2Document3", "Texte 2 du document 3")}
              />
              <Separator className="my-2" />
            </AF.Col>
          </AF.Tab>
          <AF.Tab value="contact" showHeader={false}>
            <AF.Col>
              <AF.Text name="ensemblesCoordonneesAuteurAdresse1" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurAdresse2" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurCodePostal" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurVille" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurPays" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurTelephone" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurMail" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesCoordonneesAuteurSiteWeb" />
              <Separator className="my-2" />
              <AF.Text name="ensemblesLegalsText" />
              <Separator className="my-2" />
              <AF.RichText name="ensemblesDescriptionNotesEnsemble" />
            </AF.Col>
          </AF.Tab>
          <AF.Tab value="printSettings" showHeader={false} isForm={false}>
            <PrintSettingsTab form={printFontForm} />
          </AF.Tab>
        </AF.AbstractForm>
      </div>
      {activeTab === "documents" && (
        <div className="shrink-0 w-[400px]">
          <p className="mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal">
            {vt("bo.settings.cr.logo", "Logo")}
          </p>
          <CertificateLogoUpload />
        </div>
      )}
    </div>
  );
};

type CrSettingsFormProps = {
  /** Ensemble fixe de l'instance ; absent tant que le CR n'a jamais été réglé. */
  crEnsemble?: PermanentEnsemble;
};

/**
 * Réglages du CR (ticket #1093) : édite l'ensemble fixe de l'instance — nom du
 * CR, signataire, coordonnées, textes des documents administratifs. S'il
 * n'existe pas encore, il est créé au premier enregistrement.
 */
const CrSettingsForm = ({ crEnsemble }: CrSettingsFormProps) => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const [searchParams] = useSearchParams();
  const activeInternalTab = searchParams.get("tab");

  const { mutateAsync: updatePermanentEnsemble } = useUpdatePermanentEnsemble();
  const { mutateAsync: getOrCreateCrEnsemble } = useGetOrCreateCrEnsemble();
  const { data: printFontData } = useGetPrintFont();
  const { mutateAsync: updatePrintFont } = useUpdatePrintFont();

  const form = useForm<PermanentEnsemble>({ defaultValues: crEnsemble });

  const printFontForm = useForm<{ fontFamily: PrintFontFamily }>({
    defaultValues: {
      fontFamily: printFontData?.fontFamily ?? "Arial",
    },
  });

  const resetForm = () => {
    if (crEnsemble) {
      form.reset(crEnsemble);
    }
  };

  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crEnsemble]);

  useEffect(() => {
    if (printFontData) {
      printFontForm.reset({
        fontFamily: printFontData.fontFamily ?? "Arial",
      });
    }
  }, [printFontData, printFontForm]);

  const resetPrintFontForm = () => {
    printFontForm.reset({
      fontFamily: printFontData?.fontFamily ?? "Arial",
    });
  };

  const clickReset = () => {
    if (activeInternalTab === "printSettings") {
      resetPrintFontForm();
    } else {
      resetForm();
    }
    toast({
      title: vt("settingsResetToastTitle", "Modifications annulées"),
      description: vt(
        "settingsResetToastDescription",
        "Les réglages enregistrés ont été rétablis.",
      ),
      duration: 3000,
      variant: "default",
    });
  };

  const saveForm = async (data: PermanentEnsemble) => {
    try {
      const id = data.id ?? (await getOrCreateCrEnsemble()).id;
      await updatePermanentEnsemble({ ...data, id });
      toast({
        title: t("settingsSavedToastTitle"),
        description: t("settingsSavedToastDescription"),
        duration: 3000,
        variant: "success",
      });
      form.reset({}, { keepValues: true });
    } catch {
      toast({
        title: vt("settingsErrorToastTitle", "Échec de l'enregistrement"),
        description: vt(
          "settingsErrorToastDescription",
          "Les réglages n'ont pas pu être enregistrés. Réessayez.",
        ),
        duration: 3000,
        variant: "destructive",
      });
    }
  };

  const savePrintFont = async (data: { fontFamily: PrintFontFamily }) => {
    try {
      await updatePrintFont(data);
      toast({
        title: t("settingsSavedToastTitle"),
        description: t("settingsSavedToastDescription"),
        duration: 3000,
        variant: "success",
      });
      printFontForm.reset({}, { keepValues: true });
    } catch {
      toast({
        title: vt("settingsErrorToastTitle", "Échec de l'enregistrement"),
        description: vt(
          "settingsErrorToastDescription",
          "Les réglages n'ont pas pu être enregistrés. Réessayez.",
        ),
        duration: 3000,
        variant: "destructive",
      });
    }
  };

  const handleSave = () => {
    if (activeInternalTab === "printSettings") {
      printFontForm.handleSubmit(savePrintFont)();
    } else {
      form.handleSubmit(saveForm)();
    }
  };

  return (
    <div>
      <div className="flex flex-row justify-between items-center">
        <h2 className="font-semibold font-[Instrument Sans] text-[32px]">
          {vt("bo.settings.cr.tabTitle", "Réglages du CR")}
        </h2>

        <div className="flex flex-row items-center gap-4">
          <Button variant="outline" onClick={clickReset}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave}>{t("saveButton")}</Button>
        </div>
      </div>

      <Separator className="mt-5 mb-6" />

      <EnsembleSettings form={form} printFontForm={printFontForm} />
    </div>
  );
};

type WatermarkFormContentProps = {
  instance: NonNullable<ReturnType<typeof useGetCurrentInstance>["data"]>;
};

const WatermarkFormContent = ({ instance }: WatermarkFormContentProps) => {
  const { t } = useTranslation();
  const { mutateAsync: updateInstance, isPending: updatePending } =
    useUpdateCurrentInstance();

  const form = useForm<InstanceWatermarkConfig>({
    defaultValues: getWatermarkDefaultValues(instance),
  });

  useEffect(() => {
    form.reset(getWatermarkDefaultValues(instance));
  }, [instance, form]);

  const WATERMARK_POSITIONS = [
    { value: "bottom-right", label: t("watermark.bottomRight") },
    { value: "bottom-left", label: t("watermark.bottomLeft") },
    { value: "top-right", label: t("watermark.topRight") },
    { value: "top-left", label: t("watermark.topLeft") },
    { value: "center", label: t("watermark.center") },
  ] as const;

  const WATERMARK_FONTS = [
    { value: "arial", label: "Arial" },
    { value: "helvetica", label: "Helvetica" },
    { value: "times", label: "Times" },
  ] as const;

  const saveWatermark = async (data: InstanceWatermarkConfig) => {
    try {
      await updateInstance(data);
      toast({
        title: t("settingsSavedToastTitle"),
        description: t("settingsSavedToastDescription"),
        duration: 3000,
        variant: "success",
      });
      form.reset({}, { keepValues: true });
    } catch {
      toast({
        title: t("settingsErrorToastTitle"),
        description: t("settingsErrorToastDescription"),
        duration: 3000,
        variant: "destructive",
      });
    }
  };

  const watermarkPosition = normalizePosition(form.watch("watermarkPosition"));

  useEffect(() => {
    if (watermarkPosition && !CORNER_POSITIONS.includes(watermarkPosition)) {
      form.setValue("watermarkRotate90", false);
    }
  }, [watermarkPosition, form]);

  return (
    <form
      onSubmit={form.handleSubmit(saveWatermark)}
      className="flex flex-col gap-6 max-w-[800px]"
    >
      <div className="flex items-center gap-2">
        <Switch
          id="watermarkEnabled"
          checked={form.watch("watermarkEnabled")}
          onCheckedChange={(checked) =>
            form.setValue("watermarkEnabled", !!checked)
          }
        />
        <Label htmlFor="watermarkEnabled">
          {t("watermark.watermarkEnabled")}
        </Label>
      </div>
      <Separator />
      <div className="space-y-2">
        <Label>{t("watermark.textContent")}</Label>
        <Input
          {...form.register("watermarkText")}
          placeholder={t("watermark.placeholder")}
          className="max-w-md"
        />
      </div>
      <div className="flex flex-row gap-4 items-end">
        <div className="space-y-2">
          <Label>{t("watermark.watermarkPosition")}</Label>
          <Select
            value={watermarkPosition}
            onValueChange={(v) => form.setValue("watermarkPosition", v)}
          >
            <SelectTrigger className="max-w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WATERMARK_POSITIONS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Switch
            id="watermarkRotate90"
            checked={form.watch("watermarkRotate90") ?? false}
            onCheckedChange={(checked) =>
              form.setValue("watermarkRotate90", !!checked)
            }
            disabled={!CORNER_POSITIONS.includes(watermarkPosition)}
          />
          <Label htmlFor="watermarkRotate90">{t("watermark.rotate90")}</Label>
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t("watermark.fontFamily")}</Label>
        <Select
          value={normalizeFont(form.watch("watermarkFontFamily"), "arial")}
          onValueChange={(v) => form.setValue("watermarkFontFamily", v)}
        >
          <SelectTrigger className="max-w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WATERMARK_FONTS.map(({ value, label }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("watermark.fontWeight")}</Label>
        <Select
          value={normalizeFontWeight(form.watch("watermarkFontWeight"))}
          onValueChange={(v) => form.setValue("watermarkFontWeight", v)}
        >
          <SelectTrigger className="max-w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">{t("watermark.textNormal")}</SelectItem>
            <SelectItem value="bold">{t("watermark.textBold")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("watermark.fontSize")}</Label>
        <Input
          type="number"
          min={12}
          max={72}
          {...form.register("watermarkFontSize", {
            valueAsNumber: true,
            min: 12,
            max: 72,
          })}
          className="max-w-[120px]"
        />
      </div>
      <div className="space-y-2">
        <Label>{t("watermark.opacity")}</Label>
        <Input
          type="number"
          min={10}
          max={100}
          {...form.register("watermarkOpacity", {
            valueAsNumber: true,
            min: 10,
            max: 100,
          })}
          className="max-w-[120px]"
        />
      </div>
      <div className="text-sm text-muted-foreground">
        {t("watermark.textColor")}
      </div>
      <div className="flex gap-4">
        <Button type="submit" disabled={updatePending}>
          {t("saveButton")}
        </Button>
      </div>
    </form>
  );
};

const WatermarkSettingsForm = () => {
  const { data: instance, isLoading: instanceLoading } =
    useGetCurrentInstance();

  if (instanceLoading || !instance) {
    return null;
  }

  return <WatermarkFormContent key={instance.id} instance={instance} />;
};


const DEFAULT_GENERIC_IMAGE_SETTINGS: GenericImageSettings = {
  text: "",
  fontFamily: "Arial",
  fontSize: 16,
  fontColor: "#000000",
  alignment: "center",
  backgroundColor: "#FFFFFF",
};

const GenericImageSettingsForm = () => {
  const { t } = useTranslation();
  const { data: genericImageSettings, isLoading: genericImageLoading } =
    useGetGenericImageSettings();
  const { data: availableFontsData } = useGetAvailableFonts();
  const { mutateAsync: updateGenericImageSettings, isPending: updatePending } =
    useUpdateGenericImageSettings();

  const form = useForm<GenericImageSettings>({
    defaultValues: DEFAULT_GENERIC_IMAGE_SETTINGS,
  });

  useEffect(() => {
    if (genericImageSettings) {
      form.reset(genericImageSettings);
    }
  }, [genericImageSettings, form]);

  const watchedValues = form.watch();
  const availableFonts =
    availableFontsData?.fonts?.length && availableFontsData.fonts.length > 0
      ? availableFontsData.fonts
      : FONT_OPTIONS.map(({ value }) => value);

  const resetForm = () => {
    form.reset(genericImageSettings ?? DEFAULT_GENERIC_IMAGE_SETTINGS);
    toast({
      title: t("settingsResetToastTitle"),
      description: t("settingsResetToastDescription"),
      duration: 3000,
      variant: "default",
    });
  };

  const onSubmit = async (data: GenericImageSettings) => {
    try {
      await updateGenericImageSettings(data);
      toast({
        title: t("settingsSavedToastTitle"),
        description: t("settingsSavedToastDescription"),
        duration: 3000,
        variant: "success",
      });
      form.reset({}, { keepValues: true });
    } catch {
      toast({
        title: t("settingsErrorToastTitle"),
        description: t("settingsErrorToastDescription"),
        duration: 3000,
        variant: "destructive",
      });
    }
  };

  if (genericImageLoading) {
    return null;
  }

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex gap-8 items-start max-w-[1200px]"
    >
      <div className="w-full max-w-[520px] space-y-5">
        <div className="space-y-2">
          <Label htmlFor="generic-image-text">
            {t("genericImage.form.text")}
          </Label>
          <Input
            id="generic-image-text"
            placeholder={t("genericImage.form.textPlaceholder")}
            {...form.register("text")}
          />
        </div>
        <div className="space-y-2">
          <Label>{t("genericImage.form.fontFamily")}</Label>
          <Select
            value={
              watchedValues.fontFamily ??
              DEFAULT_GENERIC_IMAGE_SETTINGS.fontFamily
            }
            onValueChange={(value) =>
              form.setValue("fontFamily", value, { shouldDirty: true })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableFonts.map((font) => (
                <SelectItem key={font} value={font}>
                  {font}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="generic-image-font-size">
            {t("genericImage.form.fontSize")}
          </Label>
          <Input
            id="generic-image-font-size"
            type="number"
            min={8}
            max={128}
            {...form.register("fontSize", {
              valueAsNumber: true,
              min: 8,
              max: 128,
            })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="generic-image-font-color">
            {t("genericImage.form.fontColor")}
          </Label>
          <Input
            id="generic-image-font-color"
            type="color"
            {...form.register("fontColor")}
            className="h-11 w-24 cursor-pointer"
          />
        </div>
        <div className="space-y-2">
          <Label>{t("genericImage.form.alignment")}</Label>
          <Select
            value={
              (watchedValues.alignment ??
                DEFAULT_GENERIC_IMAGE_SETTINGS.alignment) as GenericImageAlignment
            }
            onValueChange={(value) =>
              form.setValue("alignment", value as GenericImageAlignment, {
                shouldDirty: true,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">
                {t("genericImage.alignment.left")}
              </SelectItem>
              <SelectItem value="center">
                {t("genericImage.alignment.center")}
              </SelectItem>
              <SelectItem value="right">
                {t("genericImage.alignment.right")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="generic-image-background-color">
            {t("genericImage.form.backgroundColor")}
          </Label>
          <Input
            id="generic-image-background-color"
            type="color"
            {...form.register("backgroundColor")}
            className="h-11 w-24 cursor-pointer"
          />
        </div>
        <div className="flex items-center gap-4">
          <Button type="button" variant="outline" onClick={resetForm}>
            {t("cancel")}
          </Button>
          <Button type="submit" disabled={updatePending}>
            {t("saveButton")}
          </Button>
        </div>
      </div>

      <div className="w-full max-w-[520px] space-y-3">
        <h3 className="text-slate-700 text-base font-semibold">
          {t("genericImage.preview.title")}
        </h3>
        <div
          className="h-[484px] w-[484px] p-6 flex items-center justify-center"
          style={{
            backgroundColor:
              watchedValues.backgroundColor ??
              DEFAULT_GENERIC_IMAGE_SETTINGS.backgroundColor,
          }}
        >
          {watchedValues.text?.trim() ? (
            <p
              style={{
                fontFamily:
                  watchedValues.fontFamily ??
                  DEFAULT_GENERIC_IMAGE_SETTINGS.fontFamily,
                fontSize: `${watchedValues.fontSize ?? DEFAULT_GENERIC_IMAGE_SETTINGS.fontSize}px`,
                color:
                  watchedValues.fontColor ??
                  DEFAULT_GENERIC_IMAGE_SETTINGS.fontColor,
                width: "100%",
                textAlign:
                  watchedValues.alignment ??
                  DEFAULT_GENERIC_IMAGE_SETTINGS.alignment,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {watchedValues.text}
            </p>
          ) : (
            <p className="text-slate-500 text-sm">
              {t("genericImage.preview.emptyState")}
            </p>
          )}
        </div>
      </div>
    </form>
  );
};

export const SettingsPage = () => {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const { data: ensembles } = useGetPermanentEnsembles();

  if (!ensembles) {
    return null;
  }

  const generalTabValue = "general";
  const crTabValue = "cr";
  const genericImageTabValue = "genericImage";
  const filigraneTabValue = "filigrane";
  const defaultTab = generalTabValue;

  return (
    <div>
      <div className="flex flex-row justify-between items-center">
        <h2 className="font-semibold font-[Instrument Sans] text-[32px]">
          {t("mySettings")}
        </h2>
      </div>

      <Separator className="mt-5 mb-6" />

      <Tabs defaultValue={defaultTab} className="flex flex-col gap-5">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value={generalTabValue} className="text-xs">
            {t("settings.general.tabTitle")}
          </TabsTrigger>
          <TabsTrigger value={crTabValue} className="text-xs">
            {vt("bo.settings.cr.tabTitle", "Réglages du CR")}
          </TabsTrigger>
          <TabsTrigger value={filigraneTabValue} className="text-xs">
            {t("watermark.watermark")}
          </TabsTrigger>
          <TabsTrigger value={genericImageTabValue} className="text-xs">
            {t("genericImage.tabTitle")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={generalTabValue}>
          <GeneralSettingsTab />
        </TabsContent>
        <TabsContent value={genericImageTabValue}>
          <GenericImageSettingsForm />
        </TabsContent>
        <TabsContent value={filigraneTabValue}>
          <WatermarkSettingsForm />
        </TabsContent>
        <TabsContent value={crTabValue}>
          <CrSettingsForm crEnsemble={ensembles[0]} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
